/**
 * Wave 29 Chunk 9b — Candidate detail page.
 *
 * Shows parsed CV summary, rankings against linked positions, and
 * allows HR to change status, re-parse, or trigger re-ranking.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Loader2, RefreshCw, Trash2, UserPlus, Star, FileText,
  Sparkles, Download, ExternalLink, Eye, AlertTriangle, Briefcase,
  GraduationCap, Award, Languages, Link as LinkIcon,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";
import { EntityShell } from "../components/shell";
import { uploadUrl } from "../lib/uploads";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const getAuthToken = () => {
  try { return localStorage.getItem("hv_token") || ""; }
  catch { return ""; }
};

const STATUSES = [
  { v: "new", l: "New" },
  { v: "reviewing", l: "Reviewing" },
  { v: "shortlisted", l: "Shortlisted" },
  { v: "interview", l: "Interview" },
  { v: "hired", l: "Hired" },
  { v: "rejected", l: "Rejected" },
  { v: "withdrawn", l: "Withdrawn" },
];

const STATUS_TONES = {
  new: "blue",
  reviewing: "amber",
  shortlisted: "violet",
  interview: "violet",
  hired: "green",
  rejected: "red",
  withdrawn: "slate",
};

const RECO_COLORS = {
  shortlist: "bg-emerald-500/20 text-emerald-300",
  consider: "bg-amber-500/20 text-amber-300",
  pass: "bg-rose-500/20 text-rose-300",
};

export default function RecruitmentCandidateDetail() {
  const { user } = useAuth();
  const isHr = user && ["owner", "admin"].includes(user.role);
  const { cid } = useParams();
  const nav = useNavigate();
  const [cand, setCand] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reparsing, setReparsing] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/recruitment/candidates/${cid}`);
      setCand(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load candidate.");
    } finally { setBusy(false); }
  };

  useEffect(() => { if (user && isHr) load(); /* eslint-disable-next-line */ }, [user, isHr, cid]);

  const setStatus = async (s) => {
    setStatusSaving(true);
    try {
      await api.patch(`/recruitment/candidates/${cid}`, { status: s });
      toast.success("Status updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setStatusSaving(false); }
  };

  const reparse = async () => {
    setReparsing(true);
    try {
      await api.post(`/recruitment/candidates/${cid}/parse`);
      toast.success("CV re-parsed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-parse failed.");
    } finally { setReparsing(false); }
  };

  const del = async () => {
    try {
      await api.delete(`/recruitment/candidates/${cid}`);
      toast.success("Deleted");
      nav("/recruitment");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!isHr) return <div className="hv-card p-6 text-sm text-[#dde3ec]">HR only.</div>;
  if (busy && !cand) return <div className="hv-card p-6 text-sm text-[#9aa3b5]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>;
  if (!cand) return null;

  const parsed = cand.parsed || {};
  const rankings = cand.rankings || {};
  const positions = cand.positions || [];

  return (
    <EntityShell
      kindLabel="Candidate"
      title={cand.full_name || "Unnamed candidate"}
      status={{ label: cand.status, tone: STATUS_TONES[cand.status] || "slate" }}
      backTo="/recruitment"
      actions={
        <>
          {cand.upload_id && (
            <button onClick={reparse} disabled={reparsing} className="hv-btn hv-btn-ghost text-xs disabled:opacity-50" data-testid="candidate-reparse">
              {reparsing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Re-parse
            </button>
          )}
          {!confirmDelete && (
            <button onClick={() => setConfirmDelete(true)} className="hv-btn hv-btn-ghost text-xs text-rose-300" data-testid="candidate-delete">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          )}
          {confirmDelete && (
            <>
              <button onClick={() => setConfirmDelete(false)} className="hv-btn hv-btn-ghost text-xs" data-testid="candidate-delete-cancel">Cancel</button>
              <button onClick={del} className="hv-btn hv-btn-primary text-xs bg-rose-500 hover:bg-rose-400" data-testid="candidate-delete-confirm">
                <Trash2 className="w-3.5 h-3.5" /> Confirm delete
              </button>
            </>
          )}
        </>
      }
    >
    <div className="space-y-4 max-w-4xl" data-testid="candidate-detail-page">

      <div className="hv-card p-4 space-y-3" data-testid="candidate-meta">
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <select value={cand.status} onChange={(e) => setStatus(e.target.value)} disabled={statusSaving}
            className="hv-input text-xs" data-testid="candidate-status">
            {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
          {cand.email && <span className="text-[#cbd5e1]">{cand.email}</span>}
          {cand.phone && <span className="text-[#cbd5e1]">· {cand.phone}</span>}
          {parsed.location && <span className="text-[#9aa3b5]">· {parsed.location}</span>}
          {/* 2026-02-18 Recruitment bug — View/Download original CV. */}
          {cand.upload?.id && (
            <a
              href={uploadUrl(cand.upload.file_url || cand.upload.id)}
              target="_blank"
              rel="noreferrer"
              className="hv-btn hv-btn-ghost text-xs inline-flex items-center gap-1"
              data-testid="candidate-view-cv"
              title="Open the original CV file in a new tab"
            >
              <Eye className="w-3 h-3" /> View CV
            </a>
          )}
          {cand.upload?.id && (
            <a
              href={uploadUrl(cand.upload.file_url || cand.upload.id)}
              download={cand.upload.filename || "cv.pdf"}
              className="hv-btn hv-btn-ghost text-xs inline-flex items-center gap-1"
              data-testid="candidate-download-cv"
              title="Download the original CV"
            >
              <Download className="w-3 h-3" /> Download
            </a>
          )}
          {cand.upload?.filename && (
            <span className="text-[#9aa3b5] inline-flex items-center gap-1">
              <FileText className="w-3 h-3" /> {cand.upload.filename}
            </span>
          )}
          {/* 2026-02-18 — surface a warning chip when the candidate has a
              recorded upload_id but the underlying file has been pruned. */}
          {cand.upload_id && !cand.upload?.id && (
            <span className="inline-flex items-center gap-1 text-[11px] text-amber-300 border border-amber-400/30 bg-amber-400/[0.06] rounded px-2 py-0.5" data-testid="candidate-cv-missing">
              <AlertTriangle className="w-3 h-3" /> CV file missing — re-upload needed
            </span>
          )}
        </div>
        {parsed.headline && <div className="text-sm text-white">{parsed.headline}</div>}
        {parsed.summary && <p className="text-sm text-[#dde3ec]">{parsed.summary}</p>}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <ScoreBar label="CV completeness" value={parsed.completeness_score || 0} note={parsed.completeness_notes} />
          <ScoreBar label="CV professionalism" value={parsed.professionalism_score || 0} note={parsed.professionalism_notes} />
        </div>

        {(parsed.skills || []).length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Skills</div>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {parsed.skills.map((s, i) => <span key={i} className="px-2 py-0.5 rounded bg-white/5 text-[11px] text-[#dde3ec]">{s}</span>)}
            </div>
          </div>
        )}
        {parsed.years_experience !== undefined && (
          <div className="text-xs text-[#cbd5e1]">{parsed.years_experience}+ years experience</div>
        )}
        {(parsed.experience || []).length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-[#1FB6C1]">Show full experience</summary>
            <ul className="mt-2 space-y-2 text-[#dde3ec]">
              {parsed.experience.map((x, i) => (
                <li key={i} className="border-l-2 border-white/10 pl-2">
                  <div className="text-white">{x.role} · {x.employer}</div>
                  <div className="text-[11px] text-[#9aa3b5]">{x.start || ""} → {x.end || "present"}</div>
                  {x.summary && <div>{x.summary}</div>}
                </li>
              ))}
            </ul>
          </details>
        )}
        {(parsed.education || []).length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-[#1FB6C1]">Education</summary>
            <ul className="mt-2 space-y-1 text-[#dde3ec]">
              {parsed.education.map((x, i) => (
                <li key={i}>{x.qualification} — {x.institution} {x.year ? `(${x.year})` : ""}</li>
              ))}
            </ul>
          </details>
        )}
        {cand.note && (
          <div className="text-xs text-[#cbd5e1]">
            <span className="text-[#647087] uppercase tracking-[0.2em] text-[10px] mr-2">Note</span>
            {cand.note}
          </div>
        )}
        {/* 2026-02-18 Recruitment bug — surface the extra extracted fields
            users were missing: links, certifications, languages, awards,
            projects, references. Each is collapsible to keep the card tidy. */}
        {((parsed.certifications || []).length > 0 || (parsed.languages || []).length > 0 || (parsed.awards || []).length > 0 || (parsed.links && Object.values(parsed.links).some(Boolean)) || (parsed.projects || []).length > 0 || (parsed.tools_software || []).length > 0) && (
          <details className="text-xs" data-testid="candidate-extra-extracted">
            <summary className="cursor-pointer text-[#1FB6C1]">More extracted fields</summary>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(parsed.certifications || []).length > 0 && (
                <BulletList color="emerald" title={<span className="inline-flex items-center gap-1"><Award className="w-3 h-3" /> Certifications</span>} items={parsed.certifications} />
              )}
              {(parsed.languages || []).length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] inline-flex items-center gap-1"><Languages className="w-3 h-3" /> Languages</div>
                  <ul className="mt-1 space-y-0.5 text-[#dde3ec]">
                    {parsed.languages.map((l, i) => (
                      <li key={i}>{typeof l === "string" ? l : `${l.language} (${l.proficiency || "—"})`}</li>
                    ))}
                  </ul>
                </div>
              )}
              {(parsed.tools_software || []).length > 0 && (
                <BulletList color="cyan" title="Tools / software" items={parsed.tools_software} />
              )}
              {(parsed.awards || []).length > 0 && (
                <BulletList color="amber" title="Awards" items={parsed.awards} />
              )}
              {parsed.links && Object.values(parsed.links).some(Boolean) && (
                <div className="sm:col-span-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] inline-flex items-center gap-1"><LinkIcon className="w-3 h-3" /> Links</div>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {["linkedin", "github", "portfolio"].map((k) => parsed.links[k] && (
                      <a key={k} href={parsed.links[k]} target="_blank" rel="noreferrer" className="text-[11px] text-[#1FB6C1] hover:underline inline-flex items-center gap-1">
                        {k} <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    ))}
                    {(parsed.links.other || []).map((u, i) => (
                      <a key={i} href={u} target="_blank" rel="noreferrer" className="text-[11px] text-[#1FB6C1] hover:underline inline-flex items-center gap-1">
                        link {i + 1} <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {(parsed.projects || []).length > 0 && (
                <div className="sm:col-span-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Projects</div>
                  <ul className="mt-1 space-y-1.5 text-[#dde3ec]">
                    {parsed.projects.map((p, i) => (
                      <li key={i} className="border-l-2 border-white/10 pl-2">
                        <div className="text-white">{p.name}</div>
                        {p.description && <div>{p.description}</div>}
                        {p.url && <a href={p.url} target="_blank" rel="noreferrer" className="text-[11px] text-[#1FB6C1] hover:underline">{p.url}</a>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </details>
        )}
      </div>

      {/* 2026-02-18 Recruitment bug — standalone "Hidro's take on this CV"
          analysis, independent of any specific job opening. Always shows
          when parsed.hidro_analysis is present. */}
      {parsed.hidro_analysis && (
        <div className="hv-card p-4 space-y-3 border-[#1FB6C1]/20 bg-[#1FB6C1]/[0.03]" data-testid="candidate-hidro-analysis">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="text-sm font-semibold text-white inline-flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-[#1FB6C1]" /> Hidro's take on this CV
            </h2>
            {typeof parsed.hidro_analysis.hireability_score === "number" && !/not configured|failed/i.test(parsed.hidro_analysis.hireability_notes || "") && (
              <div className="text-[11px] inline-flex items-center gap-2">
                <span className="text-[#9aa3b5]">Hireability</span>
                <span className={"font-bold tabular-nums " + (parsed.hidro_analysis.hireability_score >= 70 ? "text-emerald-300" : parsed.hidro_analysis.hireability_score >= 40 ? "text-amber-300" : "text-rose-300")}>
                  {parsed.hidro_analysis.hireability_score}/100
                </span>
              </div>
            )}
          </div>
          {parsed.hidro_analysis.headline_pitch && (
            <p className="text-sm text-white italic">"{parsed.hidro_analysis.headline_pitch}"</p>
          )}
          {parsed.hidro_analysis.hireability_notes && (
            <p className="text-xs text-[#dde3ec]">{parsed.hidro_analysis.hireability_notes}</p>
          )}
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            {parsed.hidro_analysis.role_seniority && (
              <span className="px-2 py-0.5 rounded bg-white/[0.06] text-[#dde3ec]">Seniority: <b>{parsed.hidro_analysis.role_seniority}</b></span>
            )}
            {parsed.hidro_analysis.career_trajectory && (
              <span className="px-2 py-0.5 rounded bg-white/[0.06] text-[#dde3ec]">Trajectory: <b>{parsed.hidro_analysis.career_trajectory}</b></span>
            )}
            {(parsed.hidro_analysis.domain_expertise || []).slice(0, 3).map((d, i) => (
              <span key={i} className="px-2 py-0.5 rounded bg-[#1FB6C1]/15 text-[#1FB6C1]">{d}</span>
            ))}
          </div>
          {parsed.hidro_analysis.career_trajectory_notes && (
            <div className="text-[11px] text-[#9aa3b5]">{parsed.hidro_analysis.career_trajectory_notes}</div>
          )}
          {parsed.hidro_analysis.compensation_band_estimate_zar && (parsed.hidro_analysis.compensation_band_estimate_zar.min_monthly || parsed.hidro_analysis.compensation_band_estimate_zar.max_monthly) && (
            <div className="text-xs text-[#dde3ec]">
              <span className="text-[#647087] uppercase tracking-[0.2em] text-[10px] mr-2">Comp estimate (ZAR/month, SA market)</span>
              R{(parsed.hidro_analysis.compensation_band_estimate_zar.min_monthly || 0).toLocaleString()}–R{(parsed.hidro_analysis.compensation_band_estimate_zar.max_monthly || 0).toLocaleString()}
              {parsed.hidro_analysis.compensation_band_estimate_zar.rationale && <span className="text-[11px] text-[#9aa3b5] ml-2">· {parsed.hidro_analysis.compensation_band_estimate_zar.rationale}</span>}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <BulletList color="emerald" title="Top strengths" items={parsed.hidro_analysis.top_3_strengths} />
            <BulletList color="amber" title="Concerns" items={parsed.hidro_analysis.top_3_concerns} />
            <BulletList color="cyan" title="Interview focus" items={parsed.hidro_analysis.interview_focus_areas} />
          </div>
          {(parsed.hidro_analysis.recommended_roles || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] inline-flex items-center gap-1"><Briefcase className="w-3 h-3" /> Best-fit roles</div>
              <ul className="mt-1 space-y-1 text-xs text-[#dde3ec]">
                {parsed.hidro_analysis.recommended_roles.slice(0, 5).map((r, i) => (
                  <li key={i}><span className="text-white font-medium">{r.title}</span>{r.why ? <span className="text-[#9aa3b5]"> — {r.why}</span> : null}</li>
                ))}
              </ul>
            </div>
          )}
          {(parsed.hidro_analysis.transferable_skills || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Transferable skills</div>
              <div className="flex flex-wrap gap-1 mt-1">
                {parsed.hidro_analysis.transferable_skills.map((s, i) => (
                  <span key={i} className="px-2 py-0.5 rounded bg-white/5 text-[11px] text-[#dde3ec]">{s}</span>
                ))}
              </div>
            </div>
          )}
          {parsed.hidro_analysis.data_quality_warning && (
            <div className="text-[11px] text-amber-300 inline-flex items-start gap-1.5 border border-amber-400/30 bg-amber-400/[0.06] rounded p-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{parsed.hidro_analysis.data_quality_warning}</span>
            </div>
          )}
        </div>
      )}

      <div className="hv-card overflow-hidden" data-testid="candidate-rankings">
        <div className="px-4 py-2.5 border-b border-white/5">
          <h2 className="text-sm font-semibold text-white">Ranked positions ({positions.length})</h2>
        </div>
        {positions.length === 0 ? (
          <div className="p-6 text-[#9aa3b5] text-sm text-center">Not linked to any position.</div>
        ) : (
          <ul className="divide-y divide-white/5">
            {positions.map((p) => {
              const r = rankings[p.id] || {};
              return (
                <li key={p.id} className="px-4 py-3" data-testid={`candidate-ranking-${p.id}`}>
                  <Link to={`/recruitment/positions/${p.id}`} className="flex items-start gap-3 group">
                    <div className="w-12 h-12 rounded bg-white/10 flex items-center justify-center text-sm font-bold text-white shrink-0">
                      {typeof r.score === "number" ? r.score : "—"}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-white truncate">{p.title}</span>
                        {r.recommendation && (
                          <span className={"px-1.5 py-0.5 rounded text-[10px] " + (RECO_COLORS[r.recommendation] || "bg-white/5 text-[#dde3ec]")}>
                            {r.recommendation}
                          </span>
                        )}
                      </div>
                      {r.fit_summary && <div className="text-[12px] text-[#dde3ec] mt-1">{r.fit_summary}</div>}
                      <details className="mt-1.5 text-[11px]">
                        <summary className="cursor-pointer text-[#1FB6C1]">Full breakdown</summary>
                        <div className="mt-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <BulletList color="emerald" title="Strengths" items={r.strengths} />
                          <BulletList color="amber" title="Gaps" items={r.gaps} />
                          <BulletList color="rose" title="Red flags" items={r.red_flags} />
                        </div>
                      </details>
                    </div>
                    <Star className="w-4 h-4 text-[#647087] group-hover:text-amber-300 shrink-0 self-center" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
    </EntityShell>
  );
}

function ScoreBar({ label, value, note }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const color = pct >= 70 ? "bg-emerald-400" : pct >= 40 ? "bg-amber-400" : "bg-rose-400";
  return (
    <div>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-[#647087]">
        <span>{label}</span>
        <span className="text-[#dde3ec]">{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden mt-1">
        <div className={"h-full " + color} style={{ width: `${pct}%` }} />
      </div>
      {note && <div className="text-[11px] text-[#9aa3b5] mt-1">{note}</div>}
    </div>
  );
}

function BulletList({ color, title, items }) {
  const list = items || [];
  const klass =
    color === "emerald" ? "text-emerald-200" :
    color === "amber" ? "text-amber-200" :
    "text-rose-200";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">{title}</div>
      {list.length === 0 ? (
        <div className="text-[#647087]">—</div>
      ) : (
        <ul className={"list-disc list-inside " + klass}>
          {list.map((s, i) => <li key={i}>{s}</li>)}
        </ul>
      )}
    </div>
  );
}
